import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as AddUserIcon } from '../../assets/icons/add-user.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as GridList } from '../../assets/icons/grid-list.svg';
import { ReactComponent as TableList } from '../../assets/icons/table-list.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { RequestUserInterface, UserInterFace } from '../../interface/user-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import {
  fetchAllUsers,
  fetchListFilters,
  fetchUserActivityLogs,
  setSortData,
  setUserFilterData,
  updateUserFilters,
} from '../../redux/slice/UserManagementSlice';
import { RootState } from '../../redux/store';
import MyButton from '../../shared-components/button/Button';
import CardBox from '../../shared-components/card-box/CardBox';
import CustomTableHeaderV2 from '../../shared-components/custom-table/components/TableHeaderV2';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import Pagination from '../../shared-components/pagination/Pagination';
import SidePaneModel from '../../shared-components/table-side-panel/SidePaneModel';
import UserSidePanel from '../../shared-components/table-side-panel/UserSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys, splitArrayIntoChunks } from '../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import userApi from '../../utils/api/users';

import ActiveLogCard from './component/active-log-card';
import MultiButtonGroup from './component/MultiButtonGroup';
import UserCardBody from './component/user-card-body';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}
/**
 * UserManagement component
 *
 * This component serves as the main entry point for the user management page.
 * It renders the user management interface.
 * @returns {JSX.Element} A JSX element representing the user management page.
 */
function UserManagement() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('userManagement');
  const [openTab, setOpenTab] = useState<0 | 1>(0);
  const [position, setPosition] = useState<any>({});
  const [fullWidth, setFullWidth] = useState<any>({});
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [activeLogModal, setActiveLogModal] = useState(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const {
    users,
    page,
    loading,
    limit,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.users);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestUserInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllUsers({
          ...data,
        }),
      );
    }
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.set('userId', 'add-user');
    setSearchParameters(parameters);
    setIsEdit(true);
  };

  /**
   * User details view function
   */
  // const viewActiveLogFunction = (currentData: UserInterFace, event?: React.MouseEvent) => {
  //   if (event) {
  //     setActiveLogModal(true);
  //     if (event.clientY > 320) {
  //       setPosition({ top: `${event.clientY - 320}px`, '--card-left-position': '5%' });
  //     } else {
  //       setPosition({
  //         top: `${event.clientY + 40}px`,
  //         '--card-bottom-position': '91%',
  //         '--card-left-position': '5%',
  //       });
  //     }
  //     dispatch(fetchUserActivityLogs(currentData.id));
  //   }
  // };

  /**
   * User details view function
   */
  const viewCardActiveFunction = (
    currentData: UserInterFace,
    userIndex: number,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      let cardLeftPosition = '13%';
      let gridRowStart = 1;
      let rowCount = 4;
      const array = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      const array2 = [
        [0, 4, 8],
        [1, 5, 9],
        [2, 6],
        [3, 7],
      ];
      if (window.innerWidth > 1571) {
        rowCount = 4;
      } else if (window.innerWidth > 1333) {
        rowCount = 3;
      } else if (window.innerWidth > 945) {
        rowCount = 2;
      } else {
        rowCount = 1;
      }
      const chunks = splitArrayIntoChunks(array, rowCount);
      cardLeftPosition = `${(100 * userIndex) / rowCount / 2}%`;
      gridRowStart = chunks.findIndex((value) => value.includes(userIndex)) + 1;
      cardLeftPosition = `${(100 * gridRowStart) / rowCount / 2}%`;

      if (rowCount === 1) {
        cardLeftPosition = '50%';
      } else if (rowCount === 2) {
        cardLeftPosition = userIndex % 2 !== 0 ? '75%' : '25%';
      } else if (rowCount === 3) {
        if (userIndex && [0, 3, 6, 9].includes(userIndex)) {
          cardLeftPosition = '20%';
        } else if (userIndex && [1, 4, 7].includes(userIndex)) {
          cardLeftPosition = '50%';
        } else if (userIndex && [2, 5, 8].includes(userIndex)) {
          cardLeftPosition = '80%';
        }
      } else if (rowCount === 4) {
        if (userIndex && array2[0].includes(userIndex)) {
          cardLeftPosition = '13%';
        } else if (userIndex && array2[1].includes(userIndex)) {
          cardLeftPosition = '37.5%';
        } else if (userIndex && array2[2].includes(userIndex)) {
          cardLeftPosition = '63.5%';
        } else if (userIndex && array2[3].includes(userIndex)) {
          cardLeftPosition = '86%';
        }
      }
      setPosition({
        '--card-left-position': cardLeftPosition,
        top: '0px',
        left: '0',
        position: 'relative',
      });
      setActiveLogModal(true);
      setFullWidth({
        gridRowStart,
      });
      dispatch(fetchUserActivityLogs(currentData.id));
    }
  };

  /**
   * Delete user confirm function
   */
  const confirmUserDelete = (accountId: string) => {
    if (accountId) {
      userApi
        .deleteUser(accountId)
        .then(() => {
          setModalBoxStatus('close');
          fetchData({
            page,
            limit,
          });
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };

  /**
   * delete modal function
   * @param id
   */
  const deleteUserFunction = (id: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={id}
          title={t('deleteUserTitle')}
          description={t('deleteUserDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(userId) => userId && confirmUserDelete(userId)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Handle the change page function
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Handle the change limit function
   * @param limitCount
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: limitCount,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the sort field function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (Object.keys(sortData).length > 0) {
      dispatch(setUserFilterData(sortData));
      dispatch(setSortData(sortData));
      fetchData({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField === 'date' ? 'createdAt' : sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  };

  /**
   * Handle the open side panel function
   * @param _
   * @param row
   */
  const handleOpenSidePanel = (_: string, row: UserInterFace, isEditAction?: boolean) => {
    if (!row?._id) return;
    searchParameters.set('userId', row._id);
    if (isEditAction) {
      searchParameters.set('edit', 'true');
    } else {
      searchParameters.delete('edit');
    }
    setSearchParameters(searchParameters);
    setIsEdit(true);
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: t('userName'),
      accessorKey: 'name' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: t('role'),
      accessorKey: 'roleName' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: t('contact'),
      accessorKey: 'email' as keyof UserInterFace,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof UserInterFace,
      sortAllow: false,
      position: 'center',
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: UserInterFace) =>
        currentRow.deletedAt ? (
          <div className="user-management-status in-active">
            <div className="status-circle" />
            <p>{t('inactive')}</p>
          </div>
        ) : (
          <div className="user-management-status active">
            <div className="status-circle" />
            <p>{t('active')}</p>
          </div>
        ),
    },
    // {
    //   label: t('actions'),
    //   accessorKey: '' as keyof UserInterFace,
    //   columnWidth: 100,
    //   /**
    //    * Action button in table
    //    * @param station
    //    * @param index
    //    */
    //   customData: (currentRow: UserInterFace) => (
    //     <div className="action-button-group">
    //       <div
    //         className="action-button-item view-color"
    //         onClick={(event) => viewActiveLogFunction(currentRow, event)}
    //         role="presentation">
    //         <ActivityIcon />
    //       </div>
    //       <div
    //         className="action-button-item"
    //         onClick={() => handleOpenSidePanel('', currentRow)}
    //         role="presentation">
    //         <ActionEditIcon />
    //       </div>
    //       <div
    //         className="action-button-item delete-color"
    //         onClick={() => deleteUserFunction(currentRow.id)}
    //         role="presentation">
    //         <DeleteIcon />
    //       </div>
    //     </div>
    //   ),
    // },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('userName'), accessorKey: 'name' },
    { label: t('role'), accessorKey: 'roleName' },
    { label: t('contact'), accessorKey: 'email' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    userApi
      .fetchUsersExport(removeEmptyKeys({ ...queryParameters, sortField, sortBy }))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: UserInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'status':
                return row.deletedAt ? t('inactive') : t('active');
              case 'roleName':
                return row.role.name;
              case 'date':
                return ConvertUTCtoDate(row.createdAt);
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'users-list.csv');
          } else {
            exportPDF(headers, rows, 'users-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const userId = searchParameters.get('userId');
    if (userId) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [searchParameters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('open');
    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('users'),
          icon: <AddUserIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('users'),
          icon: <AddUserIcon />,
          count: totalResults,
        }),
      );
    }
  }, [searchParameters, totalResults]);

  return (
    <div className="user-management-content">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        header={modalBoxDetail.header}
        notification={modalBoxDetail.notificationModal}
      />
      <div>
        {openTab === 0 && (
          <>
            <CustomTable
              paginate
              loading={loading}
              limit={limit}
              header={column}
              data={users}
              currentPage={page}
              sortData={{
                sortBy,
                sortField,
              }}
              handleSortChange={handleSortFunction}
              totalPages={totalPages}
              totalResults={totalResults}
              handlePageChange={handlePageChange}
              handleLimitChange={handleLimitChange}
              sizeChangeButtonRequired
              leftTopContent={
                <>
                  <MultiButtonGroup
                    activeTab={0}
                    buttonList={[
                      {
                        icon: <TableList />,
                        /**
                         * Button function
                         * @returns
                         */
                        function: () => setOpenTab(0),
                      },
                      {
                        icon: <GridList />,
                        /**
                         * Button function
                         * @returns
                         */
                        function: () => setOpenTab(1),
                      },
                    ]}
                  />
                  <MyButton
                    label={t('addUser')}
                    onClickFunc={submit}
                    buttonType="submit"
                    svgIcon={<AddUserIcon />}
                  />
                </>
              }
              exportDataPdfOrCSV={exportDataPdfOrCSV}
              exportLoader={exportLoader}
              topHeader={false}
              filters={filters}
              filtersToDispatch={updateUserFilters}
              filterListLoading={filterListLoading}
              filterListToDispatch={fetchListFilters}
              refreshRequired
              refreshCurrentPages={handleRefresh}
              hoverLabel={t('refresh')}
              serialNo
              flexDirection
              onHiddenColumnsChange={setHiddenColumns}
              rowClickEvent={handleOpenSidePanel}
              rowClickParamsKey="userId"
              rowClickSidePanel={
                <UserSidePanel fetchIdFrom="queryParams" handleRefresh={handleRefresh} />
              }
              tableHeight="calc(100vh - 220px)"
            />
            <ActiveLogCard
              closeModal={() => setActiveLogModal(false)}
              activeLogModal={activeLogModal}
              position={position}
            />
          </>
        )}
        {openTab === 1 && (
          <>
            <SidePaneModel
              open={isEdit}
              content={<UserSidePanel fetchIdFrom="queryParams" />}
              closePanel={() => setIsEdit(false)}
            />
            <div className="card-details">
              <CustomTableHeaderV2
                flexDirection
                refreshCurrentPages={handleRefresh}
                filters={filters}
                filtersToDispatch={updateUserFilters}
                filterListToDispatch={fetchListFilters}
                filterListLoading={filterListLoading || false}
                leftTopContent={
                  <MultiButtonGroup
                    activeTab={1}
                    buttonList={[
                      {
                        icon: <TableList />,
                        /**
                         * Button function
                         * @returns
                         */
                        function: () => setOpenTab(0),
                      },
                      {
                        icon: <GridList />,
                        /**
                         * Button function
                         * @returns
                         */
                        function: () => setOpenTab(1),
                      },
                    ]}
                  />
                }
              />
              <div className="card-list">
                {activeLogModal && (
                  <div className="fixed-grid" style={fullWidth}>
                    <ActiveLogCard
                      closeModal={() => setActiveLogModal(false)}
                      activeLogModal={activeLogModal}
                      position={position}
                    />
                  </div>
                )}
                {users.map((value: UserInterFace, index: number) => (
                  <CardBox>
                    <UserCardBody
                      userDetails={value}
                      activeLog={(userId, event) => viewCardActiveFunction(userId, index, event)}
                      editFunction={(row) => handleOpenSidePanel('', row, true)}
                      menuList={[
                        {
                          content: (
                            <div className="text-danger flex-row">
                              {' '}
                              <DeleteIcon /> {t('delete')}
                            </div>
                          ),
                          /**
                           * fasdf
                           */
                          function: deleteUserFunction,
                        },
                      ]}
                    />
                  </CardBox>
                ))}
              </div>
              <div className="pagination">
                <Pagination
                  activePage={page}
                  handlePageChange={handlePageChange}
                  size={totalPages}
                  totalResults={totalResults}
                  limit={limit}
                  handleLimitChange={handleLimitChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
